"use client";
import { useState } from "react";
import {
  Box,
  Center,
  Separator,
  Flex,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  Table,
} from "@chakra-ui/react";
import { Heading } from "@/elements";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";
import { PricingGroup } from "../../elements";
import { ENTERPRISE_GPU_DATA } from "./constants";

const PRICE_UNIT = {
  HOURLY: "hourly",
  MONTHLY: "monthly",
};

export const DesktopView = ({ data, frequency }) => {
  return (
    <Box overflowX="auto" w="100%">
      <Table.Root striped border="2px solid rgb(237,242,247)" width="100%">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader textAlign="center" textTransform="none">
              vCPUs
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Memory (GB)</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">GPU</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">NVMe (GB)</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">
              {frequency === PRICE_UNIT.HOURLY ? "Hourly" : "Monthly"} Price (USD)
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {data.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell textAlign="center">{item.vcpus}</Table.Cell>
              <Table.Cell textAlign="center">{item.memory}</Table.Cell>
              <Table.Cell textAlign="center">{item.gpu}</Table.Cell>
              <Table.Cell textAlign="center">{item.nvme}</Table.Cell>
              <Table.Cell
                textAlign="center"
                fontWeight="700"
                fontSize="18px"
                color="primary.200"
              >
                {frequency === PRICE_UNIT.MONTHLY
                  ? `$${(item.hourlyPrice * 730).toFixed(2)}`
                  : `$${item.hourlyPrice}`}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const MobileView = ({ data, frequency }) => {
  return (
    <SimpleGrid columns={2} gap={6}>
      {data.map((item, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          gap={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {item.vcpus} vCPUs
            </Text>
          </Flex>

          <Separator borderColor="gray.200" />

          <Stack gap={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {item.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                GPU:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {item.gpu}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                NVMe:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {item.nvme}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                {frequency === PRICE_UNIT.HOURLY ? "Hourly" : "Monthly"} Price:{" "}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
                {frequency === PRICE_UNIT.MONTHLY
                  ? `$${(item.hourlyPrice * 730).toFixed(2)}`
                  : `$${item.hourlyPrice}`}
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const EnterpriseGpuPricingTable = ({ showHeading = true }: { showHeading?: boolean }) => {
  const [frequency, setFrequency] = useState("hourly");
  const { heading, data } = ENTERPRISE_GPU_DATA;
  const HeadingVariant = useHeadingVariant();

  return (
    <Box id="enterprise-gpu">
      {showHeading && (
        <Heading variant={HeadingVariant} textAlign="center" mb={8}>
          {heading}
        </Heading>
      )}
      <PricingGroup frequency={frequency} onChange={setFrequency} />
      <Center display={{ md: "none" }} w="full">
        <MobileView data={data} frequency={frequency} />
      </Center>
      <Box display={{ base: "none", md: "block" }}>
        <DesktopView data={data} frequency={frequency} />
      </Box>
    </Box>
  );
};

export default EnterpriseGpuPricingTable;
